import { useEffect, useState } from "react";
import { api } from "@/api";
import { AlertTriangle, FileText, Clock, ListTodo } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

export default function ActionItemsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/action-items")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center text-gray-400 text-sm py-4">Chargement...</div>;
  if (!data) return <div className="text-center text-gray-400 text-sm py-4">Aucune donnée</div>;

  const overdueInv = data.top_overdue_invoices || [];
  const urgentTasks = data.top_urgent_tasks || [];

  return (
    <div className="space-y-3" data-testid="widget-action-items">
      <div className="grid grid-cols-4 gap-1.5 text-center">
        <Stat icon={AlertTriangle} value={data.overdue_invoices_count} label="Factures retard" color="red" />
        <Stat icon={ListTodo} value={data.overdue_tasks_count} label="Tâches" color="amber" />
        <Stat icon={FileText} value={data.missing_pieces_count} label="Pièces" color="blue" />
        <Stat icon={Clock} value={data.pending_signatures_count} label="Signatures" color="violet" />
      </div>

      {overdueInv.length > 0 && (
        <div>
          <div className="text-[11px] uppercase text-gray-500 mb-1.5">Top factures en retard</div>
          <ul className="space-y-1">
            {overdueInv.slice(0, 3).map((inv) => (
              <li key={inv.id} className="flex items-center gap-2 text-xs px-2 py-1.5 bg-red-50/50 rounded" data-testid={`widget-overdue-inv-${inv.id}`}>
                <span className="font-medium text-gray-700 truncate flex-1">#{inv.invoice_number}</span>
                <span className="text-[10px] text-red-700 font-medium shrink-0">
                  {fmtDate(inv.due_date)} • {(inv.total || 0).toFixed(2)} $
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {urgentTasks.length > 0 && (
        <div>
          <div className="text-[11px] uppercase text-gray-500 mb-1.5">Tâches urgentes</div>
          <ul className="space-y-1">
            {urgentTasks.slice(0, 3).map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-xs px-2 py-1.5 bg-amber-50/50 rounded" data-testid={`widget-task-${t.id}`}>
                <span className="font-medium text-gray-700 truncate flex-1">{t.title}</span>
                {t.due_date && (
                  <span className="text-[10px] text-amber-700 shrink-0">{fmtDate(t.due_date)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {overdueInv.length === 0 && urgentTasks.length === 0 && (
        <div className="text-center py-3">
          <ListTodo className="w-6 h-6 mx-auto text-emerald-300" />
          <p className="text-xs text-emerald-600 mt-1">Tout est à jour ✓</p>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  const colorMap = {
    red:    "bg-red-50 text-red-700",
    amber:  "bg-amber-50 text-amber-700",
    blue:   "bg-blue-50 text-blue-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <div className={`rounded-lg px-1.5 py-2 ${colorMap[color]}`}>
      <Icon className="w-3 h-3 mx-auto opacity-70" />
      <div className="text-base font-bold mt-0.5">{value || 0}</div>
      <div className="text-[9px] uppercase">{label}</div>
    </div>
  );
}
